import React from 'react';
import { MapPin, Sun, Moon, Compass } from 'lucide-react';
import {
  mapaArquetiposCapitulos,
  getColdSeasons,
  getHotSeasons,
  classifyAllSeasons,
  getArchetypeDisplayName,
  COLD_SEASON_THRESHOLD
} from '../data/mapData';

// Posições fixas dos arquétipos no mapa circular
const POSITIONS = {
  peregrino: { x: 50, y: 8 },
  guardiao: { x: 8, y: 50 },
  amante: { x: 92, y: 50 },
  alquimista: { x: 50, y: 92 },
  visionario: { x: 35, y: 75 },
  encontro: { x: 75, y: 25 },
  guerreiro: { x: 25, y: 75 },
  curador: { x: 25, y: 25 },
  guardiao_sabio: { x: 18, y: 65 },
  poeta: { x: 75, y: 75 },
  profeta: { x: 65, y: 60 },
  construtor: { x: 35, y: 55 },
  companheiro: { x: 45, y: 35 },
  sabio_compassivo: { x: 55, y: 65 },
  coracao_inteiro: { x: 50, y: 50 }
};

// Paleta oficial por arquétipo raiz
const ARCHETYPE_COLORS = {
  peregrino:  { active: '#1B4FD8', dim: 'rgba(27,79,216,0.25)' },   // Azul cobalto
  guardiao:   { active: '#C084FC', dim: 'rgba(192,132,252,0.25)' },  // Lavanda
  amante:     { active: '#EC4899', dim: 'rgba(236,72,153,0.25)' },   // Rosa/magenta
  alquimista: { active: '#F59E0B', dim: 'rgba(245,158,11,0.25)' }    // Dourado
};

// Cores para estados gerais
const COLORS = {
  dominant: '#fbbf24',
  activeDual: '#60a5fa',
  silentDual: 'rgba(100,149,237,0.2)',
  activeTriple: '#f472b6',
  silentTriple: 'rgba(219,112,147,0.2)',
  coracaoActive: '#fbbf24',
  coracaoInactive: 'rgba(255,255,255,0.08)'
};

// Mapeia qual dupla/tripla herda cor de qual raiz (eixo mais forte na combinação)
const COMBO_ROOT_MAP = {
  visionario: 'alquimista',
  encontro: 'peregrino',
  guerreiro: 'guardiao',
  curador: 'amante',
  guardiao_sabio: 'guardiao',
  poeta: 'alquimista',
  profeta: 'alquimista',
  construtor: 'peregrino',
  companheiro: 'peregrino',
  sabio_compassivo: 'guardiao'
};

// Retorna a cor do círculo baseada no estado e arquétipo
const getCircleColor = (key, result, classification) => {
  const isDominant = key === result.dominantArchetype;

  // Raízes — cor do próprio arquétipo
  if (ARCHETYPE_COLORS[key]) {
    if (isDominant) return ARCHETYPE_COLORS[key].active;
    const isActive = classification.active.some(s => s.key === key);
    return isActive ? ARCHETYPE_COLORS[key].active : ARCHETYPE_COLORS[key].dim;
  }

  // Duplas/Triplas — herdam cor da raiz dominante na combinação
  const rootKey = COMBO_ROOT_MAP[key];
  if (rootKey && ARCHETYPE_COLORS[rootKey]) {
    if (isDominant) return ARCHETYPE_COLORS[rootKey].active;
    const isActive = classification.active.some(s => s.key === key);
    return isActive ? ARCHETYPE_COLORS[rootKey].active : ARCHETYPE_COLORS[rootKey].dim;
  }

  const item = [...classification.active, ...classification.silent].find(s => s.key === key);
  if (!item) return 'rgba(148,163,184,0.25)';
  const isActive = classification.active.some(s => s.key === key);
  if (item.tipo === 'dupla') return isActive ? COLORS.activeDual : COLORS.silentDual;
  if (item.tipo === 'tripla') return isActive ? COLORS.activeTriple : COLORS.silentTriple;
  return 'rgba(148,163,184,0.25)';
};

const MapaEstacoes = ({ result }) => {
  const classification = classifyAllSeasons(result);
  const coldSeasons = getColdSeasons(result.mainArchetypes);
  const hotSeasons = getHotSeasons(result.mainArchetypes);
  const displayName = getArchetypeDisplayName(result.dominantArchetype);
  const archetypeMapData = mapaArquetiposCapitulos[displayName];

  // Raízes
  const rootKeys = ['peregrino', 'guardiao', 'amante', 'alquimista'];
  const rootLabels = {
    peregrino: { label: 'Peregrino', tx: 50, ty: 2.5, rotate: null },
    guardiao: { label: 'Guardião', tx: 3, ty: 50, rotate: '-90, 3, 50' },
    amante: { label: 'Amante', tx: 97, ty: 50, rotate: '90, 97, 50' },
    alquimista: { label: 'Alquimista', tx: 50, ty: 99, rotate: null }
  };

  // Duplas e triplas
  const dualKeys = ['visionario', 'encontro', 'guerreiro', 'curador', 'guardiao_sabio', 'poeta'];
  const tripleKeys = ['profeta', 'construtor', 'companheiro', 'sabio_compassivo'];

  const pos = POSITIONS[result.dominantArchetype] || POSITIONS.peregrino;

  return (
    <div className="mt-12 mb-8 max-w-2xl mx-auto" data-testid="mapa-estacoes">
      {/* Texto de transição */}
      <div className="text-center mb-8">
        <p className="text-white/80 text-lg italic">
          "Seu resultado não é um ponto fixo — é uma posição em movimento dentro das estações do coração."
        </p>
      </div>

      {/* Título do Mapa */}
      <div className="flex justify-center mb-6">
        <div className="bg-gradient-to-r from-indigo-900/50 via-purple-900/50 to-indigo-900/50 px-6 py-3 rounded-full border border-indigo-500/20">
          <div className="flex items-center gap-3">
            <MapPin className="w-6 h-6 text-amber-400" />
            <div className="text-center">
              <h3 className="text-2xl md:text-3xl font-extrabold text-white">Bússola Viva</h3>
              <p className="text-white/60 text-xs">Mapa das Estações do Coração</p>
            </div>
          </div>
        </div>
      </div>

      {/* Mapa Visual */}
      <div className="bg-gradient-to-br from-slate-900/60 to-indigo-900/40 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-indigo-500/20">
        <div className="relative w-full max-w-md mx-auto aspect-square">
          <svg viewBox="0 0 100 100" className="w-full h-full">
            {/* Defs para glows */}
            <defs>
              <filter id="glow-gold" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="1.5" result="blur" />
                <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
              <filter id="glow-green" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="1" result="blur" />
                <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
            </defs>

            {/* Círculos de fundo */}
            <circle cx="50" cy="50" r="45" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="0.5" />
            <circle cx="50" cy="50" r="30" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="0.5" />
            <circle cx="50" cy="50" r="15" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="0.5" />

            {/* Linhas conectoras */}
            <line x1="50" y1="5" x2="50" y2="95" stroke="rgba(255,255,255,0.1)" strokeWidth="0.3" />
            <line x1="5" y1="50" x2="95" y2="50" stroke="rgba(255,255,255,0.1)" strokeWidth="0.3" />
            <line x1="15" y1="15" x2="85" y2="85" stroke="rgba(255,255,255,0.05)" strokeWidth="0.3" />
            <line x1="85" y1="15" x2="15" y2="85" stroke="rgba(255,255,255,0.05)" strokeWidth="0.3" />

            {/* RAÍZES - 4 pontos cardinais */}
            {rootKeys.map(key => {
              const p = POSITIONS[key];
              const color = getCircleColor(key, result, classification);
              const isActive = classification.active.some(s => s.key === key);
              const isDominant = key === result.dominantArchetype;
              const info = rootLabels[key];
              const r = isDominant ? 5 : isActive ? 4.5 : 3.5;
              const filterAttr = isDominant ? 'url(#glow-gold)' : isActive ? 'url(#glow-green)' : undefined;

              return (
                <g key={key} className="cursor-pointer">
                  {isActive && !isDominant && (
                    <circle cx={p.x} cy={p.y} r={r + 1.5} fill="none" stroke={color} strokeWidth="0.4" opacity="0.4" />
                  )}
                  <circle cx={p.x} cy={p.y} r={r} fill={color} filter={filterAttr} />
                  <text
                    x={info.tx}
                    y={info.ty}
                    textAnchor="middle"
                    fill={isActive || isDominant ? 'white' : 'rgba(255,255,255,0.4)'}
                    fontSize="3"
                    fontWeight="bold"
                    transform={info.rotate ? `rotate(${info.rotate})` : undefined}
                  >
                    {info.label}
                  </text>
                </g>
              );
            })}

            {/* DUPLAS - 6 pontos intermediários */}
            {dualKeys.map(key => {
              const p = POSITIONS[key];
              const color = getCircleColor(key, result, classification);
              const isActive = classification.active.some(s => s.key === key);
              const isDominant = key === result.dominantArchetype;
              const r = isDominant ? 4 : isActive ? 3.5 : 2.5;

              return (
                <g key={key}>
                  {isActive && !isDominant && (
                    <circle cx={p.x} cy={p.y} r={r + 1.2} fill="none" stroke={COLORS.activeDual} strokeWidth="0.3" opacity="0.35" />
                  )}
                  <circle cx={p.x} cy={p.y} r={r} fill={color} />
                </g>
              );
            })}

            {/* TRIPLAS - 4 pontos de convergência */}
            {tripleKeys.map(key => {
              const p = POSITIONS[key];
              const color = getCircleColor(key, result, classification);
              const isActive = classification.active.some(s => s.key === key);
              const isDominant = key === result.dominantArchetype;
              const r = isDominant ? 3.5 : isActive ? 3 : 2;

              return (
                <g key={key}>
                  {isActive && !isDominant && (
                    <circle cx={p.x} cy={p.y} r={r + 1} fill="none" stroke={COLORS.activeTriple} strokeWidth="0.3" opacity="0.35" />
                  )}
                  <circle cx={p.x} cy={p.y} r={r} fill={color} />
                </g>
              );
            })}

            {/* CENTRO - Coração Inteiro */}
            <circle
              cx="50" cy="50" r="6"
              fill={result.isCoracaoInteiro || result.dominantArchetype === 'coracao_inteiro' ? COLORS.coracaoActive : COLORS.coracaoInactive}
              stroke="rgba(255,215,0,0.3)" strokeWidth="1"
            />

            {/* Marcador "Você está aqui" - Pulso animado */}
            <g>
              <circle cx={pos.x} cy={pos.y} r="7" fill="none" stroke="#fbbf24" strokeWidth="1" opacity="0.5">
                <animate attributeName="r" from="5" to="10" dur="1.5s" repeatCount="indefinite" />
                <animate attributeName="opacity" from="0.8" to="0" dur="1.5s" repeatCount="indefinite" />
              </circle>
              <circle cx={pos.x} cy={pos.y} r="5" fill="#fbbf24" stroke="#fff" strokeWidth="1" />
            </g>
          </svg>
        </div>

        {/* Legenda */}
        <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 text-xs text-white/60 mt-6">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full bg-amber-400"></div>
            <span>Você</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#1B4FD8' }}></div>
            <span>Peregrino</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#C084FC' }}></div>
            <span>Guardião</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#F59E0B' }}></div>
            <span>Alquimista</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#EC4899' }}></div>
            <span>Amante</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'rgba(148,163,184,0.5)' }}></div>
            <span>Silenciosa</span>
          </div>
        </div>

        {/* Nome do arquétipo atual */}
        <div className="text-center mt-6">
          <p className="text-amber-400 font-bold text-lg" data-testid="archetype-name">
            {displayName}
          </p>
          <p className="text-white/60 text-sm mt-1">
            {result.isCoracaoInteiro ? 'Integração Completa' :
              archetypeMapData?.tipo === 'raiz' ? 'Arquétipo Raiz' :
              archetypeMapData?.tipo === 'dupla' ? 'Combinação Dupla' :
              archetypeMapData?.tipo === 'tripla' ? 'Convergência Tripla' : ''}
          </p>
        </div>
      </div>

      {/* ====== PANORAMA DAS ESTAÇÕES ====== */}
      <div className="mt-10 space-y-8" data-testid="panorama-estacoes">

        {/* Estações Ativas */}
        {hotSeasons.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Sun className="w-4 h-4 text-emerald-400" />
              <h4 className="text-emerald-400 text-sm font-semibold tracking-wide uppercase">
                Estações Ativas
              </h4>
            </div>
            <div className="space-y-2">
              {hotSeasons.map((season) => {
                const pct = Math.round((season.score / 40) * 100);
                const isDominant = season.key === result.dominantArchetype;
                const rootColor = ARCHETYPE_COLORS[season.key];
                const barColor = rootColor ? rootColor.active : '#60a5fa';
                return (
                  <div
                    key={season.key}
                    className={`rounded-xl p-4 border ${isDominant
                      ? 'bg-amber-500/10 border-amber-500/30'
                      : 'bg-white/5 border-white/10'
                    }`}
                    data-testid={`active-season-${season.key}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-sm" style={{ color: isDominant ? '#fcd34d' : barColor }}>
                        {season.name}
                        {isDominant && <span className="ml-2 text-xs text-amber-400/70 font-normal">(dominante)</span>}
                      </span>
                      <span className="text-white/50 text-xs">{season.score}/40 ({pct}%)</span>
                    </div>
                    {/* Barra de progresso */}
                    <div className="w-full h-1.5 bg-white/5 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{ width: `${pct}%`, backgroundColor: isDominant ? '#fbbf24' : barColor }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            {hotSeasons.length > 0 && (
              <p className="text-white/50 text-xs mt-3 italic leading-relaxed pl-1">
                Estas são as forças que você naturalmente acessa. Seus pontos de ancoragem emocional.
              </p>
            )}
          </div>
        )}

        {/* Estações Silenciosas */}
        {coldSeasons.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Moon className="w-4 h-4 text-slate-400" />
              <h4 className="text-slate-400 text-sm font-semibold tracking-wide uppercase">
                Estações Silenciosas
              </h4>
            </div>
            <div className="space-y-2">
              {coldSeasons.map((season) => {
                const pct = Math.round((season.score / 40) * 100);
                return (
                  <div
                    key={season.key}
                    className="bg-white/5 rounded-xl p-4 border border-white/10"
                    data-testid={`silent-season-${season.key}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-slate-300 font-semibold text-sm">{season.name}</span>
                      <span className="text-white/40 text-xs">{season.score}/40 ({pct}%)</span>
                    </div>
                    <div className="w-full h-1.5 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-slate-500/60" style={{ width: `${pct}%` }} />
                    </div>
                    {season.data?.descricao_frio && (
                      <p className="text-white/50 text-xs italic mt-2.5 leading-relaxed">
                        {season.data.descricao_frio}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-white/50 text-xs mt-3 italic leading-relaxed pl-1">
              Estas estações pedem atenção e desenvolvimento. As recomendações abaixo foram escolhidas para despertar o que está silencioso.
            </p>
          </div>
        )}

        {/* Caso Coração Inteiro */}
        {classification.isCoracaoInteiro && coldSeasons.length === 0 && (
          <div className="bg-gradient-to-br from-amber-900/20 to-indigo-900/20 rounded-xl p-5 border border-amber-500/20" data-testid="coracao-inteiro-panorama">
            <div className="flex items-center gap-2 mb-3">
              <Compass className="w-4 h-4 text-amber-400" />
              <h4 className="text-amber-300 text-sm font-semibold">Todas as estações em harmonia</h4>
            </div>
            <p className="text-white/70 text-sm leading-relaxed">
              Suas quatro estações caminham juntas. Não há estações silenciosas significativas — o que há é um convite para manter esse equilíbrio vivo, cultivando presença em cada uma delas.
            </p>
          </div>
        )}

        {/* Combinações Ativas (Duplas e Triplas) */}
        {(() => {
          const activeCombos = classification.active.filter(
            s => (s.tipo === 'dupla' || s.tipo === 'tripla') && !s.isDominant
          );
          if (activeCombos.length === 0) return null;

          return (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Compass className="w-4 h-4 text-blue-400" />
                <h4 className="text-blue-400 text-sm font-semibold tracking-wide uppercase">
                  Combinações Ativas
                </h4>
              </div>
              <div className="flex flex-wrap gap-2">
                {activeCombos.map((combo) => (
                  <div
                    key={combo.key}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
                      combo.tipo === 'dupla'
                        ? 'bg-blue-500/10 border-blue-500/20 text-blue-300'
                        : 'bg-pink-500/10 border-pink-500/20 text-pink-300'
                    }`}
                    data-testid={`active-combo-${combo.key}`}
                  >
                    {combo.name}
                    <span className="ml-1.5 opacity-60">{combo.score}/{combo.maxScore}</span>
                  </div>
                ))}
              </div>
              <p className="text-white/50 text-xs mt-3 italic leading-relaxed pl-1">
                Estas pontes entre arquétipos também estão presentes em você, indicando integrações naturais entre suas forças.
              </p>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default MapaEstacoes;
